import { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { initDeepLinks } from './lib/deepLinks';
import { initPushNavigation } from './lib/pushNavigation';
import { UpdateAppGate } from './components/app/UpdateAppGate';
import { RequireAuth } from './components/RequireAuth';
import { AppLayout } from './components/layout/AppLayout';
import Login from './routes/Login';
import Register from './routes/Register';
import ForgotPassword from './routes/ForgotPassword';
import ResetPassword from './routes/ResetPassword';
import ConfirmEmail from './routes/ConfirmEmail';
import CompleteProfile from './routes/CompleteProfile';
import PermissionsOnboarding from './routes/PermissionsOnboarding';
import ScanResolve from './routes/ScanResolve';
import Home from './routes/Home';
import StationDetail from './routes/StationDetail';
import QRLanding from './routes/QRLanding';
import VacuumLanding from './routes/VacuumLanding';
import QRHandler from './routes/QRHandler';
import ConfigureCharge from './routes/ConfigureCharge';
import ActiveCharge from './routes/ActiveCharge';
import VacuumOffer from './routes/VacuumOffer';
import VacuumActive from './routes/VacuumActive';
import Wallet from './routes/Wallet';
import RechargePix from './routes/RechargePix';
import Cards from './routes/Cards';
import History from './routes/History';
import Receipt from './routes/Receipt';
import Profile from './routes/Profile';
import Alianca from './routes/Alianca';
import Convite from './routes/Convite';
import Indique from './routes/Indique';
import { RequirePainel } from './components/painel/RequirePainel';
import { PainelLayout } from './components/painel/PainelLayout';
import PainelHome from './routes/painel/PainelHome';
import PainelOverview from './routes/painel/PainelOverview';
import PainelEstacoes from './routes/painel/PainelEstacoes';
import PainelSessoes from './routes/painel/PainelSessoes';
import PainelFinanceiro from './routes/painel/PainelFinanceiro';
import PainelRede from './routes/painel/PainelRede';

/** Deep links nativos (App Links/Universal Links + br.app.bow://): registra
 *  o listener DENTRO do Router — o navigate precisa do contexto, e o cold
 *  start (getLaunchUrl) só navega com as rotas montadas. No-op no web/PWA e
 *  em shells 1.1.x sem o plugin (guards no initDeepLinks). */
function DeepLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    void initDeepLinks((path) => navigate(path));
  }, [navigate]);
  return null;
}

/** Navegação ao tocar numa notificação push. Irmão do DeepLinkHandler e pelo
 *  mesmo motivo: o navigate precisa do contexto do Router. O evento é retido
 *  pelo Capacitor até haver listener, então o cold start (app fechado, usuário
 *  toca na notificação) também cai aqui. */
function PushNavigationHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    void initPushNavigation((path) => navigate(path));
  }, [navigate]);
  return null;
}

export default function App() {
  return (
    <>
      <DeepLinkHandler />
      <PushNavigationHandler />
      {/* Bloqueio por versão nativa antiga. Fora do <Routes> de propósito:
          precisa valer também no /login e no /qr público — o AppLayout só
          envolve rota autenticada. No-op na web e quando a versão é
          desconhecida (falha aberta). */}
      <UpdateAppGate />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Confirmação de troca de email — pública (o link chega no email novo;
          o usuário pode nem estar logado neste device). */}
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      {/* Landing pública por QR físico no carregador (1 segmento, sem auth).
          Convive com /qr/:stationId/:connectorId (deeplink autenticado) — o
          React Router 6 desambigua por número de segmentos. */}
      <Route path="/qr/:stationId" element={<QRLanding />} />
      {/* Landing pública do QR físico do ASPIRADOR (Pix avulso sem conta).
          Logado → redireciona pro fluxo com saldo. */}
      <Route path="/v/:stationId" element={<VacuumLanding />} />
      {/* Landing pública do convite (Indique e Ganhe): guarda o código e
          manda criar conta; logado, vai pra Home e o ReferralClaimer aplica. */}
      <Route path="/convite/:code" element={<Convite />} />
      {/* Complete-profile é protegido por auth mas sem AppLayout (sem sidebar)
          e sem o guard de profile_complete — é exatamente a tela que o resolve. */}
      <Route
        path="/complete-profile"
        element={
          <RequireAuth>
            <CompleteProfile />
          </RequireAuth>
        }
      />
      {/* Onboarding único de permissões (app nativo) — fora do AppLayout,
          mesmo padrão do complete-profile. Gatilho no RequireAuth. */}
      <Route
        path="/onboarding/permissoes"
        element={
          <RequireAuth>
            <PermissionsOnboarding />
          </RequireAuth>
        }
      />
      {/* Painel do parceiro (CPO terceiro / investidor) — moldura própria, fora
          da BottomNav (não é tela de motorista). Só leitura; o backend revalida
          o vínculo em toda chamada. */}
      <Route
        element={
          <RequireAuth>
            <RequirePainel>
              <PainelLayout />
            </RequirePainel>
          </RequireAuth>
        }
      >
        <Route path="/painel" element={<PainelHome />} />
        <Route path="/painel/rede" element={<PainelRede />} />
        <Route path="/painel/:orgId" element={<PainelOverview />} />
        <Route path="/painel/:orgId/estacoes" element={<PainelEstacoes />} />
        <Route path="/painel/:orgId/sessoes" element={<PainelSessoes />} />
        <Route path="/painel/:orgId/financeiro" element={<PainelFinanceiro />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/stations/:stationId" element={<StationDetail />} />
        <Route path="/qr/:stationId/:connectorId" element={<QRHandler />} />
        {/* Scanner in-app: QR físico de estação lido de dentro do app cai
            aqui (1 conector = auto-start; N = picker do ConfigureCharge). */}
        <Route path="/scan/:hubStationId" element={<ScanResolve />} />
        <Route path="/configure/:pointId/:connectorId" element={<ConfigureCharge />} />
        <Route path="/charging/:sessionId" element={<ActiveCharge />} />
        <Route path="/amenities/:stationId/vacuum" element={<VacuumOffer />} />
        <Route path="/amenities/sessions/:sessionId" element={<VacuumActive />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/wallet/recharge" element={<RechargePix />} />
        {/* T3 — cartões salvos. Só serve pra abastecer a carteira; o
            backend gateia a rota por `card_save_enabled` e a tela cai no
            estado "em breve" enquanto a flag estiver desligada. */}
        <Route path="/wallet/cards" element={<Cards />} />
        <Route path="/history" element={<History />} />
        <Route path="/receipt/:sessionId" element={<Receipt />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/alianca" element={<Alianca />} />
        <Route path="/indique" element={<Indique />} />
      </Route>
      </Routes>
    </>
  );
}
